/**
 * Utility functions for UTM parameter tracking
 */

export interface UTMParams {
  source?: string;
  campaign?: string;
  medium?: string;
  referrer_url?: string;
}

/**
 * Extract UTM parameters from current URL
 */
const extractUTMFromURL = (): UTMParams => {
  if (typeof window === 'undefined') {
    return {};
  }

  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};

  const source = urlParams.get('utm_source');
  const campaign = urlParams.get('utm_campaign');
  const medium = urlParams.get('utm_medium');

  if (source) utmParams.source = source;
  if (campaign) utmParams.campaign = campaign;
  if (medium) utmParams.medium = medium;

  // Store full referrer URL with UTM params
  // Prefer the current URL with UTM params, otherwise use document.referrer
  if (window.location.search && (source || campaign || medium)) {
    // If we have UTM params in current URL, store the full URL
    utmParams.referrer_url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  } else if (document.referrer) {
    // Otherwise use the referrer (where they came from)
    utmParams.referrer_url = document.referrer;
  } else if (window.location.search) {
    // Fallback: store current URL even without UTM params
    utmParams.referrer_url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }

  return utmParams;
};

/**
 * Store UTM parameters in localStorage for later use
 * (useful if UTM params are on signup page but org is created later)
 */
export const storeUTMInLocalStorage = (): void => {
  if (typeof window === 'undefined') return;

  const utmParams = extractUTMFromURL();
  if (Object.keys(utmParams).length > 0) {
    localStorage.setItem('utm_params', JSON.stringify(utmParams));
  }
};

/**
 * Retrieve UTM parameters from localStorage
 */
const getUTMFromLocalStorage = (): UTMParams => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = localStorage.getItem('utm_params');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.error('Error reading UTM params from localStorage:', err);
  }

  return {};
};

/**
 * Clear UTM parameters from localStorage
 */
export const clearUTMFromLocalStorage = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('utm_params');
};

/**
 * Get UTM parameters (from URL first, then localStorage)
 */
export const getUTMParams = (): UTMParams => {
  const urlParams = extractUTMFromURL();
  if (Object.keys(urlParams).length > 0) {
    return urlParams;
  }
  return getUTMFromLocalStorage();
};
